import type { ReactNode } from "react";
import { CategoryHeader, fadeUp, vars } from "@pack-it-up/ui-web";
import type { Person } from "./people.js";
import type { ShortcutRegistry } from "@pack-it-up/viewmodel";
import type { SortableHandleProps } from "../../features/dnd/Sortable.js";
import { AddRow } from "./AddRow.js";
import type { ItemComposerDraft } from "./ItemComposer.js";
import { DragHandle } from "./DragHandle.js";

// A collapsible category rendered as the design-system CategoryBlock: a bordered card
// whose header (caret, name, count) sits over its rows and a rapid-add row. Collapse
// state is persisted (Doc 10 §5 / Doc 06 §2) via `onToggleCollapse` →
// categoriesStore.toggleCollapsed.
export const CategorySection = ({
  name,
  count,
  collapsed,
  canEdit,
  onToggleCollapse,
  onAdd,
  people,
  onAddItem,
  itemVariant,
  onRename,
  onDelete,
  onCheckAll,
  onUncheckAll,
  tagPalette,
  // When provided, the category header gets an interactive drag handle (manual sort only).
  // `listeners` and `attributes` come from dnd-kit useSortable on the wrapping element.
  dragHandleProps,
  addShortcut,
  leading,
  children,
}: {
  name: string;
  count: string | number;
  collapsed: boolean;
  canEdit: boolean;
  onToggleCollapse: () => void;
  onAdd: (name: string) => void;
  // Full kit item composer (T-G) — passed through to AddRow when rendering items
  // (packing or todos); omit to fall back to the bare text-field add row.
  // Assignable people for the add composer: trip members, or a template's participants.
  people?: Person[];
  // Tags in use on this trip/template (T29) — offered in the add composer.
  tagPalette?: string[];
  onAddItem?: (draft: ItemComposerDraft) => void;
  // "todo" swaps the composer's qty stepper for a due-date field.
  itemVariant?: "item" | "todo";
  // Editor-only rename handler (click-to-edit).
  onRename?: (name: string) => void;
  // Editor-only "⋮ → Delete category" intent; the page owns the confirm + cascade.
  onDelete?: () => void;
  // "More" menu bulk actions: check/uncheck every eligible row in this category for the
  // current user. Omit either to hide that item.
  onCheckAll?: () => void;
  onUncheckAll?: () => void;
  // dnd-kit sortable props for the category drag handle (manual sort + editor only).
  dragHandleProps?: SortableHandleProps;
  // Doc 06 §8: passed on the "current" (first) category so its AddRow binds
  // Cmd/Ctrl+Enter → start adding an item there.
  addShortcut?: ShortcutRegistry;
  // T28 sync mode: the category's tri-state tick, rendered in the header's leading slot.
  leading?: ReactNode;
  children: ReactNode;
}): ReactNode => (
  <div
    className={fadeUp}
    style={{
      border: `1px solid ${vars.color.border}`,
      borderRadius: vars.radius.md,
      background: vars.color.surface,
      overflow: "hidden",
      marginBottom: vars.space.md,
    }}
  >
    <div style={{ borderBottom: collapsed ? "none" : `1px solid ${vars.color.border}` }}>
      <CategoryHeader
        name={name}
        count={count}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        canEdit={canEdit}
        {...(onRename !== undefined ? { onRename } : {})}
        {...(onDelete !== undefined ? { onDelete } : {})}
        {...(onCheckAll !== undefined ? { onCheckAll } : {})}
        {...(onUncheckAll !== undefined ? { onUncheckAll } : {})}
        {...(leading !== undefined ? { leading } : {})}
        {...(dragHandleProps !== undefined
          ? {
              dragHandle: (
                <DragHandle
                  listeners={dragHandleProps.listeners}
                  attributes={dragHandleProps.attributes}
                />
              ),
            }
          : {})}
      />
    </div>
    {!collapsed && (
      <>
        {children}
        {canEdit && (
          <AddRow
            label={`Add item to ${name}`}
            onAdd={onAdd}
            {...(people !== undefined ? { people } : {})}
            {...(tagPalette !== undefined ? { tagPalette } : {})}
            {...(onAddItem !== undefined ? { onAddItem } : {})}
            {...(itemVariant !== undefined ? { itemVariant } : {})}
            {...(addShortcut !== undefined ? { shortcutRegistry: addShortcut } : {})}
          />
        )}
      </>
    )}
  </div>
);
